import React, { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { uploadImageToStorage } from '../supabaseClient';
import './AdEditor.css';

const AdEditor = ({ ad, onClose, onUpdateGenerations, onSaveEditedAd, editHistory: savedEditHistory, onUpdateEditHistory, isDemoMode = false, onDemoAction }) => {
  const { getIdTokenClaims } = useAuth0();
  const [editHistory, setEditHistory] = useState(
    savedEditHistory || [
      {
        id: ad.id,
        imageUrl: ad.imageUrl,
        prompt: ad.prompt,
        isOriginal: true
      }
    ]
  );
  const [inputValue, setInputValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);
  const historyEndRef = useRef(null);

  // Save edit history to parent whenever it changes
  useEffect(() => {
    if (onUpdateEditHistory) {
      onUpdateEditHistory(ad.id, editHistory);
    }
  }, [editHistory, ad.id, onUpdateEditHistory]);

  // Auto-scroll to bottom when new edits are added
  useEffect(() => {
    historyEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [editHistory]);

  const handleEdit = async () => {
    if (!inputValue.trim() || isEditing) return;

    // If in demo mode, just show auth popup
    if (isDemoMode && onDemoAction) {
      onDemoAction();
      return;
    }

    const editPrompt = inputValue.trim();
    const currentImage = editHistory[editHistory.length - 1];

    setIsEditing(true);

    try {
      // Get Auth0 ID token for API authentication
      const tokenClaims = await getIdTokenClaims();
      const accessToken = tokenClaims.__raw; // Get the raw JWT token

      // Preserve the original aspect ratio
      const dimensionSpec = ad.width && ad.height
        ? `IMPORTANT: Maintain the exact dimensions of ${ad.width}x${ad.height} pixels (aspect ratio ${ad.aspectRatio || '1:1'}). The edited image MUST be ${ad.width} pixels wide and ${ad.height} pixels tall.`
        : '';

      const response = await fetch('/api/generate-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          model: 'google/gemini-2.5-flash-image',
          messages: [
            {
              role: 'user',
              content: [
                {
                  type: 'text',
                  text: dimensionSpec ? `${dimensionSpec}\n\n${editPrompt}` : editPrompt
                },
                {
                  type: 'image_url',
                  image_url: {
                    url: currentImage.imageUrl
                  }
                }
              ]
            }
          ],
          temperature: 0.7,
          max_tokens: 1000
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`API request failed: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      console.log('Edit response:', data);
      
      // Extract base64 image from response
      let imageUrl = null;
      if (data.choices?.[0]?.message?.images?.[0]?.image_url?.url) {
        imageUrl = data.choices[0].message.images[0].image_url.url;
      } else if (data.choices?.[0]?.message?.content) {
        console.warn('No image returned, got text response:', data.choices[0].message.content);
        throw new Error('No image returned from API');
      }

      // Upload edited image to Supabase Storage
      const editId = `edit_${Date.now()}_${Math.random()}`;
      console.log('📤 [Supabase] Uploading edited image to storage...');
      const publicUrl = await uploadImageToStorage(imageUrl, editId);
      
      if (publicUrl) {
        console.log('✅ [Supabase] Edited image uploaded, public URL:', publicUrl);
        imageUrl = publicUrl; // Use public URL instead of data URL
      } else {
        console.warn('⚠️ [Supabase] Upload failed, using data URL as fallback');
      }

      // Generate caption for the edited image
      let caption = '';
      try {
        console.log('🏷️ Generating caption for edited image...');
        const captionResponse = await fetch('/api/generate-image', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify({
            model: 'google/gemini-2.5-flash-image',
            messages: [
              {
                role: 'user',
                content: [
                  {
                    type: 'text',
                    text: 'Write a short, catchy caption (10-15 words max) for this advertisement image. Just return the caption text, nothing else.'
                  },
                  {
                    type: 'image_url',
                    image_url: {
                      url: imageUrl
                    }
                  }
                ]
              }
            ],
            temperature: 0.7,
            max_tokens: 50
          })
        });

        if (captionResponse.ok) {
          const captionData = await captionResponse.json();
          caption = captionData.choices?.[0]?.message?.content || '';
          console.log('✅ Caption generated:', caption);
        }
      } catch (error) {
        console.error('Failed to generate caption:', error);
      }

      // Add the edited image to history (preserve aspect ratio from original)
      const newEdit = {
        id: editId,
        imageUrl: imageUrl,
        caption: caption,
        prompt: editPrompt,
        aspectRatio: ad.aspectRatio || '1:1',
        width: ad.width || 1024,
        height: ad.height || 1024,
        isOriginal: false,
        createdAt: new Date().toISOString(),
        model: 'google/gemini-2.5-flash-image'
      };

      setEditHistory(prev => [...prev, newEdit]);
      
      // Add edited image to the main grid
      if (onSaveEditedAd) {
        onSaveEditedAd(ad.id, newEdit);
      }
      
      // Increment generation count (each edit counts as 1 generation)
      if (onUpdateGenerations) {
        onUpdateGenerations(1);
      }

      // Clear input only after successful edit
      setInputValue('');

    } catch (error) {
      console.error('Error editing ad:', error);
      alert(`Failed to edit ad: ${error.message}. Please try again.`);
    } finally {
      setIsEditing(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEdit();
    }
  };

  const handleDownload = async (edit) => {
    if (!edit.imageUrl) {
      alert('Image not available for download');
      return;
    }

    try {
      // Fetch the image as a blob
      const response = await fetch(edit.imageUrl);
      const blob = await response.blob();
      
      // Create a blob URL
      const blobUrl = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `aris-ad-edited-${edit.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download image. Please try again.');
    }
  };

  return (
    <div className="ad-editor-overlay">
      <div className="ad-editor-container">
        {/* Close Button */}
        <button className="editor-close-button" onClick={onClose}>
          ×
        </button>

        {/* Left Side - Image Display */}
        <div className="editor-left">
          <div className="editor-image-container">
            {editHistory.length > 0 && editHistory[editHistory.length - 1].imageUrl ? (
              <img 
                src={editHistory[editHistory.length - 1].imageUrl} 
                alt="Current edit" 
                className="editor-image"
              />
            ) : (
              <div className="editor-image-placeholder">
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎨</div>
                <div>No image available</div>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Edit History & Chat */}
        <div className="editor-right">
          <h2 className="editor-title">Edit Your Ad</h2>
          
          {/* Edit History */}
          <div className="edit-history">
            {editHistory.map((edit, index) => (
              <div key={edit.id} className="edit-item">
                <div className="edit-header">
                  <span className="edit-label">
                    {edit.isOriginal ? 'Original' : `Edit ${index}`}
                  </span>
                  <button 
                    className="edit-download-btn"
                    onClick={() => handleDownload(edit)}
                    title="Download this version"
                  >
                    ⬇
                  </button>
                </div>
                <div className="edit-prompt">
                  {edit.isOriginal ? `Original prompt: ${edit.prompt}` : edit.prompt}
                </div>
                {edit.imageUrl && (
                  <div className="edit-thumbnail" onClick={() => {
                    // Show this version as the main image
                    const clickedIndex = editHistory.findIndex(e => e.id === edit.id);
                    if (clickedIndex !== editHistory.length - 1) {
                      // Move this version to the end (show it as current)
                      const newHistory = [...editHistory];
                      const [movedItem] = newHistory.splice(clickedIndex, 1);
                      newHistory.push({...movedItem, id: `view_${Date.now()}`});
                      setEditHistory(newHistory);
                    }
                  }}>
                    <img src={edit.imageUrl} alt={`Edit ${index}`} />
                  </div>
                )}
              </div>
            ))}
            <div ref={historyEndRef} />
          </div>

          {/* Input Area */}
          <div className="editor-input-container">
            <textarea
              ref={inputRef}
              className="editor-input"
              placeholder="Describe what you want to change... (e.g., 'Make the background blue', 'Add text saying Sale!')"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isEditing}
              rows={3}
            />
            <button
              className={`editor-submit-button ${isEditing ? 'loading' : ''}`}
              onClick={handleEdit}
              disabled={isEditing || !inputValue.trim()}
            >
              {isEditing ? (
                <>
                  <span className="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                  Editing...
                </>
              ) : (
                'Edit Ad →'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdEditor;

